import React, { useCallback, useEffect, useMemo, useState, lazy, Suspense } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Search, Edit, Trash2, Plus, History, Shield, Users, FolderOpen, Save, X, AlertTriangle, CheckCircle, Clock, Eye, ArrowLeft, UserCheck, Calendar, FileText, ClipboardList, LogOut, TrendingUp, Briefcase, Package, HelpCircle, GraduationCap, DollarSign, Home, ChevronsUpDown, Check, UserCog, Filter, Loader2, Download } from 'lucide-react';
import MultiSelectCombobox from './MultiSelectCombobox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

// Import Portal Components for Impersonation
import AttendancePortalEmbed from './portals/AttendancePortalEmbed';
import LeavePortalEmbed from './portals/LeavePortalEmbed';
import OvertimePortalEmbed from './portals/OvertimePortalEmbed';
import SelfHelpPortalEmbed from './portals/SelfHelpPortalEmbed';
import ExitPortalEmbed from './portals/ExitPortalEmbed';
import PerformancePortalEmbed from './portals/PerformancePortalEmbed';
import TrainingPortalEmbed from './portals/TrainingPortalEmbed';
import AssetPortalEmbed from './portals/AssetPortalEmbed';
import PayslipPortalEmbed from './portals/PayslipPortalEmbed';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SuperAdminPortal = ({ user }) => {
  const [employees, setEmployees] = useState([]);
  const [portals, setPortals] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedPortal, setSelectedPortal] = useState('');
  // Employee-picker UX state — dynamic text search across name / employee_id
  // / email / department so a SuperAdmin doesn't have to scroll through
  // hundreds of employees to find one.
  const [empPickerOpen, setEmpPickerOpen] = useState(false);
  const [empSearch, setEmpSearch] = useState('');
  const [entries, setEntries] = useState([]);
  const [editableFields, setEditableFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [activeTab, setActiveTab] = useState('manage');
  
  // Impersonation mode state
  const [impersonationMode, setImpersonationMode] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  
  // Dialog states
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [createForm, setCreateForm] = useState({});
  const [deletingEntry, setDeletingEntry] = useState(null);
  const [saving, setSaving] = useState(false);

  // Employee name for display
  const [selectedEmployeeName, setSelectedEmployeeName] = useState('');

  useEffect(() => {
    fetchPortals();
    fetchEmployees();
  }, []);

  const fetchPortals = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/superadmin/portals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPortals(response.data);
    } catch (error) {
      toast.error('Failed to load portals');
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/superadmin/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to load employees');
    }
  };

  const fetchEntries = async () => {
    if (!selectedEmployee || !selectedPortal) {
      toast.error('Please select both Employee and Portal');
      return;
    }

    setLoadingEntries(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/superadmin/entries/${selectedPortal}/${selectedEmployee}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEntries(response.data.entries || []);
      setEditableFields(response.data.editable_fields || []);
      setSelectedEmployeeName(response.data.employee_name || '');
      toast.success(`Loaded ${response.data.total} entries`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load entries');
      setEntries([]);
    } finally {
      setLoadingEntries(false);
    }
  };

  const fetchAuditLogs = async () => {
    setLoadingLogs(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/superadmin/audit-logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 100 }
      });
      setAuditLogs(response.data.logs || []);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  // Start impersonation mode
  const startImpersonation = () => {
    if (!selectedEmployee || !selectedPortal) {
      toast.error('Please select both Employee and Portal');
      return;
    }
    
    const employee = employees.find(e => e.employee_id === selectedEmployee);
    if (!employee) {
      toast.error('Employee not found');
      return;
    }
    
    // Create impersonated user object with employee's details
    setImpersonatedUser({
      id: employee.user_id || employee.id,
      employee_id: employee.employee_id,
      name: employee.name,
      email: employee.email,
      role: employee.role || 'employee',
      department: employee.department,
      designation: employee.designation,
      reporting_manager: employee.reporting_manager,
      // Mark as impersonated for audit purposes
      _impersonated: true,
      _impersonated_by: user.email
    });
    
    setImpersonationMode(true);
    toast.success(`Now viewing ${selectedPortal.replace('_', ' ')} as ${employee.name}`);
  };

  // Exit impersonation mode
  const exitImpersonation = () => {
    setImpersonationMode(false);
    setImpersonatedUser(null);
    toast.info('Exited employee view mode');
  };

  const handleEditClick = (entry) => {
    setEditingEntry(entry);
    setEditForm({ ...entry });
    setShowEditDialog(true);
  };

  const handleCreateClick = () => {
    setCreateForm({});
    setShowCreateDialog(true);
  };

  const handleDeleteClick = (entry) => {
    setDeletingEntry(entry);
    setShowDeleteDialog(true);
  };

  // Payroll-scope only: stream the payslip PDF to the browser using the
  // shared ``/api/payslips/{id}/pdf`` endpoint. Super Admin already has
  // payroll access via ``has_payroll_access`` on the backend so no extra
  // permission plumbing is needed.
  const handleDownloadPayslipPdf = async (entry) => {
    if (!entry?.id) {
      toast.error('Payslip has no ID');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/payslips/${entry.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const label = `Payslip_${entry.employee_code || ''}_${entry.month || ''}_${entry.year || ''}`.replace(/\s+/g, '_');
      a.download = `${label}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download payslip');
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/superadmin/entries/update`, {
        portal: selectedPortal,
        record_id: editingEntry.id,
        employee_id: selectedEmployee,
        data: editForm
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry updated successfully');
      setShowEditDialog(false);
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update entry');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/superadmin/entries/create`, {
        portal: selectedPortal,
        employee_id: selectedEmployee,
        data: createForm
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry created successfully');
      setShowCreateDialog(false);
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/superadmin/entries/delete`, {
        headers: { Authorization: `Bearer ${token}` },
        data: {
          portal: selectedPortal,
          record_id: deletingEntry.id,
          employee_id: selectedEmployee
        }
      });
      toast.success('Entry deleted successfully');
      setShowDeleteDialog(false);
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'completed': 'bg-blue-100 text-blue-800',
      'draft': 'bg-gray-100 text-gray-800',
      'pending_rm': 'bg-orange-100 text-orange-800',
      'pending_hr': 'bg-purple-100 text-purple-800',
      'pending_admin': 'bg-indigo-100 text-indigo-800'
    };
    return statusColors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const formatFieldName = (field) => {
    return field
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getEntryDisplayFields = () => {
    const portalFields = {
      'attendance': ['date', 'status', 'approval_status', 'submitted_at'],
      'leave': ['leave_type', 'from_date', 'to_date', 'approval_status'],
      'overtime': ['start_date', 'end_date', 'total_overtime', 'approval_status'],
      'self_help': ['category', 'status', 'submitted_at'],
      'exit': ['submission_date', 'reason_for_leaving', 'status'],
      'performance': ['evaluation_month', 'evaluation_year', 'total_percentage'],
      'appraisal': ['fiscal_year', 'status', 'final_score'],
      'performance_evaluation': ['evaluation_month', 'evaluation_year', 'status', 'submitted_at'],
      'training_assignment': ['training_title', 'deadline', 'status', 'assigned_at'],
      'payroll': ['month', 'year', 'cycle_from', 'cycle_to', 'monthly_base', 'net_pay', 'generated_at'],
    };
    return portalFields[selectedPortal] || ['id', 'created_at', 'status'];
  };

  const renderFieldInput = (field, value, onChange, isCreate = false) => {
    if (field.includes('date') || field.includes('_at')) {
      return (
        <Input
          type="datetime-local"
          value={value ? new Date(value).toISOString().slice(0, 16) : ''}
          onChange={(e) => onChange(field, e.target.value)}
          className="w-full"
        />
      );
    }
    if (field === 'status' || field === 'approval_status') {
      return (
        <Select value={value || ''} onValueChange={(val) => onChange(field, val)}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="pending_rm">Pending RM</SelectItem>
            <SelectItem value="pending_hr">Pending HR</SelectItem>
            <SelectItem value="pending_admin">Pending Admin</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    if (field === 'leave_type') {
      return (
        <Select value={value || ''} onValueChange={(val) => onChange(field, val)}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select leave type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="casual">Casual Leave</SelectItem>
            <SelectItem value="sick">Sick Leave</SelectItem>
            <SelectItem value="earned">Earned Leave</SelectItem>
            <SelectItem value="comp_off">Compensatory Off</SelectItem>
            <SelectItem value="lop">Loss of Pay</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    if (field === 'hours' || field === 'work_hours' || field.includes('score') || field.includes('rank')) {
      return (
        <Input
          type="number"
          value={value || ''}
          onChange={(e) => onChange(field, parseFloat(e.target.value) || 0)}
          className="w-full"
        />
      );
    }
    if (field === 'reason' || field === 'remarks' || field === 'comments' || field.includes('description')) {
      return (
        <Textarea
          value={value || ''}
          onChange={(e) => onChange(field, e.target.value)}
          className="w-full"
          rows={3}
        />
      );
    }
    return (
      <Input
        type="text"
        value={typeof value === 'object' ? JSON.stringify(value) : (value || '')}
        onChange={(e) => onChange(field, e.target.value)}
        className="w-full"
      />
    );
  };

  const renderActionBadge = (action) => {
    const colors = {
      'create': 'bg-green-100 text-green-800',
      'update': 'bg-blue-100 text-blue-800',
      'delete': 'bg-red-100 text-red-800'
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  // Portal configuration for impersonation
  const portalInfo = {
    'attendance': { 
      icon: Calendar, 
      color: 'blue',
      description: 'Mark daily attendance, view monthly calendar, and track attendance status',
      Component: AttendancePortalEmbed
    },
    'leave': { 
      icon: FileText, 
      color: 'green',
      description: 'Apply for leave, view leave balance, and track leave requests',
      Component: LeavePortalEmbed
    },
    'overtime': { 
      icon: Clock, 
      color: 'orange',
      description: 'Log overtime hours and view overtime history',
      Component: OvertimePortalEmbed
    },
    'self_help': { 
      icon: HelpCircle, 
      color: 'purple',
      description: 'Raise tickets, track requests, and access self-service options',
      Component: SelfHelpPortalEmbed
    },
    'exit': { 
      icon: LogOut, 
      color: 'red',
      description: 'Initiate resignation, view exit status, and complete clearance',
      Component: ExitPortalEmbed
    },
    'performance': { 
      icon: TrendingUp, 
      color: 'indigo',
      description: 'View performance evaluations, goals, and feedback',
      Component: PerformancePortalEmbed
    },
    'training': { 
      icon: GraduationCap, 
      color: 'teal',
      description: 'Complete assigned trainings and earn certificates',
      Component: TrainingPortalEmbed
    },
    'assets': { 
      icon: Package, 
      color: 'cyan',
      description: 'View assigned company assets and equipment',
      Component: AssetPortalEmbed
    },
    'payslip': { 
      icon: DollarSign, 
      color: 'emerald',
      description: 'View and download monthly payslips',
      Component: PayslipPortalEmbed
    }
  };

  // Render the impersonated portal view
  const renderImpersonatedPortal = () => {
    if (!impersonatedUser) return null;

    const portal = portalInfo[selectedPortal];
    const PortalIcon = portal?.icon || FileText;
    const PortalComponent = portal?.Component;

    return (
      <div className="space-y-6">
        {/* Employee Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Employee Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500">Employee ID</p>
                <p className="font-medium">{impersonatedUser.employee_id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">{impersonatedUser.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Department</p>
                <p className="font-medium">{impersonatedUser.department || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Designation</p>
                <p className="font-medium">{impersonatedUser.designation || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{impersonatedUser.email || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Role</p>
                <p className="font-medium capitalize">{impersonatedUser.role || 'Employee'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Reporting Manager</p>
                <p className="font-medium">{impersonatedUser.reporting_manager || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Portal Content */}
        <Card className="border-gray-200">
          <CardHeader className="bg-gray-50 border-b">
            <CardTitle className="flex items-center gap-2 text-gray-700">
              <PortalIcon className="h-5 w-5" />
              {portals.find(p => p.key === selectedPortal)?.name}
            </CardTitle>
            <CardDescription>{portal?.description}</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {PortalComponent ? (
              <Suspense fallback={
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              }>
                <PortalComponent 
                  impersonatedUser={impersonatedUser}
                  adminUser={user}
                />
              </Suspense>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <FolderOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Portal component not available for impersonation mode.</p>
                <p className="text-sm mt-2">Use &quot;Load Entries&quot; to view and manage data directly.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  // If in impersonation mode, show the portal with back button
  if (impersonationMode && impersonatedUser) {
    return (
      <div className="space-y-4">
        {/* Impersonation Banner */}
        <Card className="bg-purple-50 border-purple-300">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <UserCheck className="h-5 w-5 text-purple-600" />
                <div>
                  <p className="font-medium text-purple-800">
                    Viewing as: {impersonatedUser.name} ({impersonatedUser.employee_id})
                  </p>
                  <p className="text-sm text-purple-600">
                    Portal: {portals.find(p => p.key === selectedPortal)?.name} | Department: {impersonatedUser.department || 'N/A'}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={exitImpersonation}
                className="border-purple-300 text-purple-700 hover:bg-purple-100"
                data-testid="exit-impersonation-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Exit Employee View
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Render the actual portal */}
        {renderImpersonatedPortal()}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Shield className="h-8 w-8 text-purple-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Super Admin Portal</h1>
          <p className="text-sm text-gray-500">Manage and edit employee portal entries</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-2xl">
          <TabsTrigger value="manage" className="flex items-center gap-2" data-testid="sa-tab-manage">
            <FolderOpen className="h-4 w-4" />
            Manage Entries
          </TabsTrigger>
          <TabsTrigger value="manager-actions" className="flex items-center gap-2" data-testid="sa-tab-manager-actions">
            <UserCog className="h-4 w-4" />
            Manager Actions
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-2" onClick={fetchAuditLogs} data-testid="sa-tab-audit">
            <History className="h-4 w-4" />
            Audit Trail
          </TabsTrigger>
        </TabsList>

        <TabsContent value="manage" className="space-y-6 mt-6">
          {/* Selection Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Select Employee & Portal
              </CardTitle>
              <CardDescription>
                Choose an employee and portal to view and edit their entries, or use the portal as the employee
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Employee</Label>
                  <Popover open={empPickerOpen} onOpenChange={setEmpPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between font-normal"
                        data-testid="employee-select"
                      >
                        <span className="truncate text-left">
                          {selectedEmployee
                            ? (() => {
                                const e = employees.find(x => x.employee_id === selectedEmployee);
                                return e ? `${e.employee_id} - ${e.name}` : selectedEmployee;
                              })()
                            : 'Select employee'}
                        </span>
                        <ChevronsUpDown className="w-4 h-4 opacity-60 flex-shrink-0 ml-2" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-h-[420px] overflow-hidden" align="start">
                      <div className="flex items-center border-b px-3">
                        <Search className="w-4 h-4 opacity-60 mr-2 flex-shrink-0" />
                        <input
                          autoFocus
                          value={empSearch}
                          onChange={(e) => setEmpSearch(e.target.value)}
                          placeholder="Search by name, ID, email, department…"
                          className="flex-1 py-2 outline-none text-sm bg-transparent"
                          data-testid="employee-select-search"
                        />
                      </div>
                      {(() => {
                        const q = empSearch.trim().toLowerCase();
                        const filtered = q
                          ? employees.filter(e =>
                              e.name?.toLowerCase().includes(q) ||
                              e.employee_id?.toLowerCase().includes(q) ||
                              e.email?.toLowerCase().includes(q) ||
                              e.department?.toLowerCase().includes(q)
                            )
                          : employees;
                        return (
                          <>
                            <div className="px-3 py-1.5 border-b text-xs bg-gray-50 text-gray-500">
                              Showing {filtered.length} of {employees.length}
                            </div>
                            <div className="overflow-y-auto max-h-[320px]" data-testid="employee-select-list">
                              {filtered.length === 0 ? (
                                <div className="p-4 text-sm text-gray-500" data-testid="employee-select-empty">
                                  No matches.
                                </div>
                              ) : (
                                filtered.map((emp) => {
                                  const on = emp.employee_id === selectedEmployee;
                                  return (
                                    <button
                                      type="button"
                                      key={emp.employee_id}
                                      onClick={() => {
                                        setSelectedEmployee(emp.employee_id);
                                        setEmpPickerOpen(false);
                                        setEmpSearch('');
                                      }}
                                      className={`w-full flex items-start gap-3 px-3 py-2 hover:bg-gray-50 border-b last:border-b-0 text-left ${on ? 'bg-emerald-50' : ''}`}
                                      data-testid={`employee-option-${emp.employee_id}`}
                                    >
                                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${on ? 'text-emerald-600' : 'text-transparent'}`} />
                                      <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-gray-900 truncate">
                                          {emp.employee_id} - {emp.name}
                                        </div>
                                        <div className="text-xs text-gray-500 truncate">
                                          {emp.department || 'No dept'}{emp.email ? ` · ${emp.email}` : ''}
                                        </div>
                                      </div>
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>
                    Portal
                    {selectedPortal && portals.find(p => p.key === selectedPortal)?.scope === 'manager' && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded bg-purple-100 text-purple-700">
                        Manager scope · picks records this person AUTHORED
                      </span>
                    )}
                  </Label>
                  <Select value={selectedPortal} onValueChange={setSelectedPortal}>
                    <SelectTrigger data-testid="portal-select">
                      <SelectValue placeholder="Select portal" />
                    </SelectTrigger>
                    <SelectContent>
                      {portals.map((portal) => (
                        <SelectItem key={portal.key} value={portal.key}>
                          <span className="flex items-center gap-2">
                            {portal.name}
                            {portal.scope === 'manager' && (
                              <span className="text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                                manager
                              </span>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-2 justify-end">
                  <Button 
                    onClick={fetchEntries} 
                    disabled={!selectedEmployee || !selectedPortal || loadingEntries}
                    className="w-full"
                    data-testid="load-entries-btn"
                  >
                    {loadingEntries ? 'Loading...' : 'Load Entries'}
                  </Button>
                  <Button 
                    onClick={startImpersonation} 
                    disabled={!selectedEmployee || !selectedPortal}
                    variant="outline"
                    className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                    data-testid="use-as-employee-btn"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Use Portal as Employee
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Entries Table */}
          {entries.length > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    {selectedEmployeeName}&apos;s {portals.find(p => p.key === selectedPortal)?.name} Entries
                  </CardTitle>
                  <CardDescription>
                    {entries.length} entries found. Click edit to modify or delete entries.
                  </CardDescription>
                </div>
                <Button onClick={handleCreateClick} className="flex items-center gap-2" data-testid="create-entry-btn" disabled={selectedPortal === 'payroll'} title={selectedPortal === 'payroll' ? 'Payslips are generated from Pay Distribution runs — not manually' : ''}>
                  <Plus className="h-4 w-4" />
                  Create Entry
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {getEntryDisplayFields().map((field) => (
                          <TableHead key={field}>{formatFieldName(field)}</TableHead>
                        ))}
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map((entry, idx) => (
                        <TableRow key={entry.id || idx}>
                          {getEntryDisplayFields().map((field) => {
                            let cellValue = entry[field];
                            // Payroll payslips store the payable amount inside a
                            // ``totals`` object — surface it as a first-class column.
                            if (selectedPortal === 'payroll' && field === 'net_pay') {
                              const raw = entry?.totals?.net_pay ?? 0;
                              cellValue = `₹${Number(raw).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
                            }
                            if (selectedPortal === 'payroll' && field === 'monthly_base' && typeof cellValue === 'number') {
                              cellValue = `₹${Number(cellValue).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
                            }
                            return (
                            <TableCell key={field}>
                              {field === 'status' || field === 'approval_status' ? (
                                <Badge className={getStatusBadge(entry[field])}>
                                  {entry[field] || '-'}
                                </Badge>
                              ) : field.includes('date') || field.includes('_at') ? (
                                formatDate(entry[field])
                              ) : typeof cellValue === 'object' && cellValue !== null ? (
                                JSON.stringify(cellValue)?.substring(0, 50) + '...'
                              ) : (
                                cellValue ?? '-'
                              )}
                            </TableCell>
                            );
                          })}
                          <TableCell className="text-right space-x-2">
                            {selectedPortal === 'payroll' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadPayslipPdf(entry)}
                                data-testid={`payslip-pdf-${idx}`}
                                title="Download payslip PDF"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            )}
                            {editableFields.length > 0 && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditClick(entry)}
                                data-testid={`edit-entry-${idx}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteClick(entry)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-entry-${idx}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {entries.length === 0 && selectedEmployee && selectedPortal && !loadingEntries && (
            <Card>
              <CardContent className="py-10 text-center">
                <FolderOpen className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No entries found for this employee in the selected portal.</p>
                <div className="flex gap-4 justify-center mt-4">
                  <Button onClick={handleCreateClick} data-testid="create-first-entry-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create First Entry
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={startImpersonation}
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Use Portal as Employee
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="manager-actions" className="space-y-6 mt-6">
          <ManagerActionsTab employees={employees} />
        </TabsContent>

        <TabsContent value="audit" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Audit Trail
              </CardTitle>
              <CardDescription>
                Complete history of all changes made by Super Admin
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingLogs ? (
                <div className="text-center py-8">Loading audit logs...</div>
              ) : auditLogs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No audit logs found</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Timestamp</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Portal</TableHead>
                        <TableHead>Target Employee</TableHead>
                        <TableHead>Performed By</TableHead>
                        <TableHead>Changes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditLogs.map((log, idx) => (
                        <TableRow key={log.id || idx}>
                          <TableCell className="whitespace-nowrap">
                            {formatDate(log.created_at)}
                          </TableCell>
                          <TableCell>
                            <Badge className={renderActionBadge(log.action)}>
                              {log.action?.toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell>{log.portal}</TableCell>
                          <TableCell>
                            {log.target_employee_name} ({log.target_employee_id})
                          </TableCell>
                          <TableCell>{log.performed_by_email}</TableCell>
                          <TableCell className="max-w-xs truncate">
                            {JSON.stringify(log.changes)?.substring(0, 100)}...
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Entry
            </DialogTitle>
            <DialogDescription>
              Modify the entry fields below. Changes will be logged in the audit trail.
            </DialogDescription>
          </DialogHeader>
          {editingEntry && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              {editableFields.map((field) => (
                <div key={field} className="space-y-2">
                  <Label>{formatFieldName(field)}</Label>
                  {renderFieldInput(
                    field,
                    editForm[field],
                    (f, v) => setEditForm(prev => ({ ...prev, [f]: v }))
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Create New Entry
            </DialogTitle>
            <DialogDescription>
              Create a new entry for {selectedEmployeeName} in {portals.find(p => p.key === selectedPortal)?.name}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            {editableFields.map((field) => (
              <div key={field} className="space-y-2">
                <Label>{formatFieldName(field)}</Label>
                {renderFieldInput(
                  field,
                  createForm[field],
                  (f, v) => setCreateForm(prev => ({ ...prev, [f]: v })),
                  true
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? 'Creating...' : 'Create Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Delete
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this entry? This action will be logged and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deletingEntry && (
            <div className="bg-red-50 p-4 rounded-lg my-4">
              <p className="text-sm text-red-800">
                <strong>Entry ID:</strong> {deletingEntry.id}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving ? 'Deleting...' : 'Delete Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminPortal;

// ============= Manager Actions Tab =====================================

/**
 * ManagerActionsTab — SuperAdmin-only override centre for manager-side
 * decisions (leaves, attendance, performance) + a reporting-manager
 * reassignment form. All writes flow through backend endpoints that persist
 * an ``audit_logs`` entry so every override is traceable.
 *
 * Layout:
 *   1. Reassign Manager card — pick employee + new manager + reason, PUT
 *      ``/api/superadmin/override/manager``.
 *   2. Manager Actions Audit list — GET ``/api/superadmin/manager-actions-audit``
 *      with employee/manager/portal/action filters.
 */
const ManagerActionsTab = ({ employees = [] }) => {
  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // ---- Reassign manager form ------------------------------------------
  const [employeeId, setEmployeeId] = useState('');
  const [managerId, setManagerId] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // ---- Audit feed -----------------------------------------------------
  const [feed, setFeed] = useState([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterManager, setFilterManager] = useState('');
  const [filterPortal, setFilterPortal] = useState('');

  const activeEmployees = useMemo(
    () => (employees || []).filter((e) => e.is_active !== false),
    [employees],
  );

  const employeeOptions = useMemo(() => activeEmployees.map((e) => ({
    value: e.employee_id || e.id,
    label: `${e.employee_id || e.id} · ${e.name}`,
    sublabel: `${e.department || ''}${e.designation ? ' · ' + e.designation : ''}`,
  })), [activeEmployees]);

  const managerOptions = useMemo(() => activeEmployees
    .filter((e) => ['manager', 'admin', 'superadmin'].includes((e.role || '').toLowerCase()) || e.is_manager)
    .map((e) => ({
      value: e.id,
      label: `${e.employee_id || e.id} · ${e.name}`,
      sublabel: `${e.department || ''}${e.role ? ' · ' + e.role : ''}`,
    })), [activeEmployees]);

  const fetchFeed = useCallback(async () => {
    setFeedLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterEmployee) params.set('employee_id', filterEmployee);
      if (filterManager) params.set('manager_id', filterManager);
      if (filterPortal) params.set('portal', filterPortal);
      params.set('limit', '100');
      const res = await axios.get(`${API}/superadmin/manager-actions-audit?${params}`, config);
      setFeed(res.data.logs || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load audit feed');
    } finally {
      setFeedLoading(false);
    }
  }, [filterEmployee, filterManager, filterPortal]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  const handleReassign = async () => {
    if (!employeeId) { toast.error('Pick an employee first'); return; }
    setSaving(true);
    setLastResult(null);
    try {
      const res = await axios.put(
        `${API}/superadmin/override/manager`,
        { employee_id: employeeId, new_manager_id: managerId || null, reason: reason || null },
        config,
      );
      setLastResult(res.data);
      toast.success(res.data.message || 'Manager reassigned');
      setReason('');
      fetchFeed();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reassign failed');
    } finally {
      setSaving(false);
    }
  };

  const portalLabels = {
    leave: 'Leave',
    attendance: 'Attendance',
    overtime: 'Overtime',
    performance: 'Performance',
    appraisal: 'Appraisal',
    team_hierarchy: 'Team Hierarchy',
  };

  return (
    <div className="space-y-6" data-testid="manager-actions-tab">
      {/* Reassign Manager */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5 text-indigo-600" />
            Reassign Reporting Manager
          </CardTitle>
          <CardDescription>
            Move an employee to a different reporting manager. Every reassignment is written
            to the audit log with your name and reason.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label>Employee</Label>
            <MultiSelectCombobox
              options={employeeOptions.map((o) => ({ ...o }))}
              selected={employeeId ? [employeeId] : []}
              onChange={(vals) => setEmployeeId(vals[vals.length - 1] || '')}
              placeholder="Search employee…"
              searchPlaceholder="Type name / ID / department"
              triggerTestId="reassign-employee-picker"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>New Reporting Manager <span className="text-xs text-slate-500">(leave empty to detach)</span></Label>
            <MultiSelectCombobox
              options={managerOptions}
              selected={managerId ? [managerId] : []}
              onChange={(vals) => setManagerId(vals[vals.length - 1] || '')}
              placeholder="Search manager…"
              searchPlaceholder="Type manager name / ID"
              triggerTestId="reassign-manager-picker"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Reason (audit trail)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Team restructuring effective Q3"
              data-testid="reassign-reason-input"
            />
          </div>
          <div className="md:col-span-2 flex items-center gap-3">
            <Button
              onClick={handleReassign}
              disabled={saving || !employeeId}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="reassign-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserCog className="w-4 h-4 mr-2" />}
              Reassign Manager
            </Button>
            {lastResult && (
              <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-1.5">
                {lastResult.employee_name} · {lastResult.old_manager || 'none'} → {lastResult.new_manager || 'none'}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Audit feed */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-5 w-5 text-indigo-600" />
            Manager Actions Audit
          </CardTitle>
          <CardDescription>
            Every override performed on manager-side workflows — approvals reversed, dates
            edited, records deleted, or a reporting manager reassigned.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Employee</Label>
              <Input
                value={filterEmployee}
                onChange={(e) => setFilterEmployee(e.target.value)}
                placeholder="Employee code (e.g. GBT042)"
                data-testid="audit-filter-employee"
              />
            </div>
            <div>
              <Label className="text-xs">Manager (UUID)</Label>
              <Input
                value={filterManager}
                onChange={(e) => setFilterManager(e.target.value)}
                placeholder="Manager UUID"
                data-testid="audit-filter-manager"
              />
            </div>
            <div>
              <Label className="text-xs">Portal</Label>
              <select
                value={filterPortal}
                onChange={(e) => setFilterPortal(e.target.value)}
                className="w-full h-10 border rounded px-2 text-sm bg-white"
                data-testid="audit-filter-portal"
              >
                <option value="">All</option>
                {Object.entries(portalLabels).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={fetchFeed}
                disabled={feedLoading}
                data-testid="audit-refresh"
                className="w-full"
              >
                {feedLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Filter className="w-4 h-4 mr-2" />}
                Refresh
              </Button>
            </div>
          </div>

          {feedLoading ? (
            <div className="py-8 text-center text-slate-500">
              <Loader2 className="w-5 h-5 mx-auto animate-spin" />
            </div>
          ) : feed.length === 0 ? (
            <div className="py-8 text-center text-slate-500 border border-dashed rounded">
              No override actions match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-600 uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left px-3 py-2">Action</th>
                    <th className="text-left px-3 py-2">Portal</th>
                    <th className="text-left px-3 py-2">Employee</th>
                    <th className="text-left px-3 py-2">By</th>
                    <th className="text-left px-3 py-2">Changes</th>
                  </tr>
                </thead>
                <tbody data-testid="manager-actions-audit-body">
                  {feed.map((lg, i) => {
                    const when = lg.created_at
                      ? new Date(lg.created_at).toLocaleString('en-IN', { hour12: false })
                      : '—';
                    return (
                      <tr key={lg.id || i} className="border-t hover:bg-slate-50">
                        <td className="px-3 py-2 whitespace-nowrap text-slate-600">{when}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="uppercase text-[10px]">{lg.action || '—'}</Badge>
                        </td>
                        <td className="px-3 py-2">{portalLabels[lg.portal] || lg.portal}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium">{lg.target_employee_name || '—'}</div>
                          <div className="text-xs text-slate-500">{lg.target_employee_id}</div>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600">{lg.performed_by_email}</td>
                        <td className="px-3 py-2">
                          <ChangesBlock changes={lg.changes} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const ChangesBlock = ({ changes }) => {
  if (!changes || Object.keys(changes).length === 0) return <span className="text-xs text-slate-400">—</span>;
  return (
    <div className="text-xs space-y-1 max-w-md">
      {Object.entries(changes).map(([k, v]) => {
        if (v && typeof v === 'object' && ('old' in v || 'new' in v)) {
          return (
            <div key={k} className="flex items-baseline gap-1">
              <span className="font-semibold text-slate-600">{k}:</span>
              <span className="text-red-600 line-through">{JSON.stringify(v.old ?? '—')}</span>
              <span className="text-slate-400">→</span>
              <span className="text-emerald-700">{JSON.stringify(v.new ?? '—')}</span>
            </div>
          );
        }
        return (
          <div key={k}>
            <span className="font-semibold text-slate-600">{k}:</span>{' '}
            <span className="text-slate-700">{typeof v === 'string' ? v : JSON.stringify(v)}</span>
          </div>
        );
      })}
    </div>
  );
};

